import React from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  useTheme,
} from '@mui/material';
import {
  SearchOff as SearchOffIcon,
  FolderOpen as FolderOpenIcon,
  ErrorOutline as ErrorOutlineIcon,
  CloudOff as CloudOffIcon,
  Add as AddIcon,
} from '@mui/icons-material';

/**
 * Enterprise-grade EmptyState component
 * Provides contextual empty states with helpful guidance
 *
 * @param {Object} props
 * @param {string} props.type - Type of empty state: 'no-data', 'no-results', 'error', 'offline', 'custom'
 * @param {string} props.title - Primary message
 * @param {string} props.description - Detailed description or guidance
 * @param {React.ReactNode} props.icon - Custom icon
 * @param {string} props.actionLabel - Label for primary action button
 * @param {Function} props.onAction - Handler for primary action
 * @param {string} props.secondaryActionLabel - Label for secondary action
 * @param {Function} props.onSecondaryAction - Handler for secondary action
 * @param {React.ReactNode} props.children - Custom content
 */
const EmptyState = ({
  type = 'no-data',
  title,
  description,
  icon,
  actionLabel,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
  children,
}) => {
  const theme = useTheme();

  const getDefaultContent = () => {
    switch (type) {
      case 'no-results':
        return {
          icon: <SearchOffIcon sx={{ fontSize: 64 }} />,
          title: title || 'No results found',
          description: description || 'Try adjusting your search or filter criteria',
        };
      case 'error':
        return {
          icon: <ErrorOutlineIcon sx={{ fontSize: 64 }} />,
          title: title || 'Something went wrong',
          description: description || 'We encountered an error loading this data',
        };
      case 'offline':
        return {
          icon: <CloudOffIcon sx={{ fontSize: 64 }} />,
          title: title || 'Connection lost',
          description: description || 'Please check your internet connection',
        };
      case 'no-data':
      default:
        return {
          icon: <FolderOpenIcon sx={{ fontSize: 64 }} />,
          title: title || 'No data available',
          description: description || 'Get started by creating your first item',
        };
    }
  };

  const content = getDefaultContent();
  const displayIcon = icon || content.icon;
  const displayTitle = title || content.title;
  const displayDescription = description || content.description;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 400,
        px: 3,
        py: 6,
        textAlign: 'center',
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          color: theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.3)'
            : 'rgba(0, 0, 0, 0.2)',
          mb: 3,
        }}
      >
        {displayIcon}
      </Box>

      {/* Title */}
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          color: 'text.primary',
          mb: 1,
        }}
      >
        {displayTitle}
      </Typography>

      {/* Description */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          maxWidth: 500,
          mb: 3,
        }}
      >
        {displayDescription}
      </Typography>

      {/* Actions */}
      {(actionLabel || secondaryActionLabel || children) && (
        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
          {actionLabel && onAction && (
            <Button
              variant="contained"
              onClick={onAction}
              startIcon={type === 'no-data' ? <AddIcon /> : undefined}
            >
              {actionLabel}
            </Button>
          )}
          {secondaryActionLabel && onSecondaryAction && (
            <Button
              variant="outlined"
              onClick={onSecondaryAction}
            >
              {secondaryActionLabel}
            </Button>
          )}
          {children}
        </Stack>
      )}
    </Box>
  );
};

export default EmptyState;
